import {
  getUserRoleSalesOrderRelatedPermissionGroupAssignments,
  PermissionsGroupName,
} from '@zspace/roles';
import {
  SalesOrder,
  User,
  UserRoleAssignment,
  UserRoleType,
} from '@zspace/types';
import { useCallback, useMemo } from 'react';
import SalesOrdersList from '../../sales-orders/sales-orders-list/sales-orders-list';
import useUser from '../../shared/hooks/user';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import {
  Column,
  ExpandableRow,
  OnTableChangeFunction,
} from '../../ui/table/types';
import { UserStatusText } from '../types';
import { PermissionsGroupBySalesOrder } from '../users-table/users-table';

export type ReadOnlyUsersTableProps = CustomPaginatedTableProps<User> & {
  onChange: OnTableChangeFunction<User, PermissionsGroupBySalesOrder>;
  relatedAccountId: string;
};

export function ReadOnlyUsersTable({
  dataSource,
  onChange,
  config,
  empty,
  form,
  relatedAccountId,
}: ReadOnlyUsersTableProps) {
  const { user: currentUser } = useUser();

  const getUserPermissionsGroup = useCallback(
    (user: User): PermissionsGroupName | null => {
      let userPermissionsGroup: PermissionsGroupName | null = null;
      let userRole: UserRoleAssignment | undefined;

      if (currentUser.activeRole?.role.name === UserRoleType.ZSPACE_INTERNAL) {
        userRole = user.roles.find((userRoleAssignment) =>
          userRoleAssignment.permissionsGroupUserRoleAssignments.find(
            (permissionGroupAssignment) =>
              permissionGroupAssignment.salesOrder?.opportunity?.account?.id ===
              relatedAccountId
          )
        );
      } else {
        userRole = user.roles.find(
          (userRoleAssignment) =>
            userRoleAssignment.role.name === currentUser.activeRole?.role.name
        );
      }

      if (userRole) {
        const permissionsGroupUserRoleAssignment =
          userRole.permissionsGroupUserRoleAssignments[0];

        if (permissionsGroupUserRoleAssignment) {
          userPermissionsGroup = permissionsGroupUserRoleAssignment
            .permissionsGroup.name as PermissionsGroupName;
        }
      }

      return userPermissionsGroup;
    },
    [currentUser.activeRole?.role.name, relatedAccountId]
  );

  const isExpandableRow = useCallback((row: User) => {
    const userRoles = new Set<PermissionsGroupName>();
    row.roles.forEach((userRoleAssignment) => {
      userRoleAssignment.permissionsGroupUserRoleAssignments.forEach(
        (permissionsGroupUserRoleAssignment) => {
          userRoles.add(
            permissionsGroupUserRoleAssignment.permissionsGroup
              .name as PermissionsGroupName
          );
        }
      );
    });
    return userRoles.size > 1;
  }, []);

  const columns: Column<User, PermissionsGroupBySalesOrder>[] = useMemo(
    () => [
      {
        key: 'email',
        className: 'is-width-25',
        render: (el) => el.email,
        title: 'User',
        sortable: true,
      },
      {
        key: 'role',
        className: 'is-width-25',
        render: (el) => {
          const permissionGroup = getUserPermissionsGroup(el);
          if (isExpandableRow(el) || !permissionGroup) {
            return null;
          }
          return <span>{permissionGroup}</span>;
        },
        title: 'Role',
        expandableContentRender: (el) => el.permissionsGroupName,
      },
      {
        key: 'salesOrders',
        className: 'is-width-30',
        render: (el) => {
          if (isExpandableRow(el)) {
            return null;
          }
          const parsedSalesOrdersSet = el.roles.reduce(
            (acc, roleAssignment) => {
              roleAssignment.permissionsGroupUserRoleAssignments.forEach(
                (permissionsGroupUserRoleAssignment) => {
                  if (permissionsGroupUserRoleAssignment.salesOrder) {
                    acc.add(permissionsGroupUserRoleAssignment.salesOrder);
                  }
                }
              );
              return acc;
            },
            new Set<SalesOrder>()
          );

          const parsedSalesOrders = Array.from(parsedSalesOrdersSet.values());
          const link = `/users/${el.id}`;

          return (
            <SalesOrdersList
              list={parsedSalesOrders}
              textColor="primary"
              hasMoreLink={link}
              openLinksInNewTab
            />
          );
        },
        title: 'Sales orders',
        sortable: false,
        expandableContentRender: (el, parentElement) => {
          if (!el.salesOrders) {
            return null;
          }

          const salesOrders = el.salesOrders;
          const link = `/users/${parentElement.id}`;

          return (
            <SalesOrdersList
              list={salesOrders}
              textColor="primary"
              hasMoreLink={link}
              openLinksInNewTab
            />
          );
        },
      },
      {
        key: 'deletedAt',
        className: 'is-width-15',
        render: (el) => {
          const userStatus = el.lastActive
            ? UserStatusText.ACTIVE
            : UserStatusText.PENDING_FIRST_LOGIN;

          return <span>{userStatus}</span>;
        },
        title: 'Status',
        sortable: false,
      },
    ],
    [getUserPermissionsGroup, isExpandableRow]
  );

  const expandableRow = useMemo(():
    | ExpandableRow<User, PermissionsGroupBySalesOrder>
    | undefined => {
    return {
      expandable: (row: User) => isExpandableRow(row),
      expandableRowIterator: (row: User) => {
        const permissionsGroupsBySalesOrderMap = new Map<
          PermissionsGroupName,
          PermissionsGroupBySalesOrder
        >();

        const userSalesOrderRelatedPermissionGroupsAssignments =
          row.roles.flatMap((userRoleAssignment) =>
            getUserRoleSalesOrderRelatedPermissionGroupAssignments(
              userRoleAssignment
            )
          );

        if (!userSalesOrderRelatedPermissionGroupsAssignments) {
          return [];
        }

        userSalesOrderRelatedPermissionGroupsAssignments.forEach(
          (assignment) => {
            const permissionsGroupName = assignment.permissionsGroup
              .name as PermissionsGroupName;

            const existingGroup =
              permissionsGroupsBySalesOrderMap.get(permissionsGroupName);
            const salesOrders = existingGroup
              ? [...existingGroup.salesOrders, assignment.salesOrder!]
              : [assignment.salesOrder!];

            permissionsGroupsBySalesOrderMap.set(permissionsGroupName, {
              permissionsGroupName,
              salesOrders,
            });
          }
        );

        return Array.from(permissionsGroupsBySalesOrderMap.values());
      },
    };
  }, [isExpandableRow]);

  return (
    <PaginatedTable
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      config={config}
      empty={empty}
      rowKey={'id'}
      form={form}
      expandableRow={expandableRow}
    />
  );
}

export default ReadOnlyUsersTable;
